<template>
    <div class="channel-connect-wrapper">
        <div class="connect-label">{{ connectionTitle }}</div>
        <steps-overview ref="stepOverview" :step="stepId + ''" :steps="steps" ></steps-overview>
        <div class="forms-wrapper">
                <div class="form-error" v-if="isError">There was an error. Please try again.</div>
                <div class="form-error-msg" v-if="isError">Error: {{ errorMessage }}</div>
                <template v-for="(form, index) in getCurrentStepFormArray()">
                    <div class="form-step-wrapper" :key="index">
                        <div class="form-element" v-if="form.type == 'html-text'" v-html="form.content"></div>
                        <div class="form-element" v-if="form.type == 'html-anchor'"><a class="link-important" target="_blank" :href="form.href">{{form.content}}</a></div>
                        <div class="form-element" v-if="form.type == 'button-primary'">
                            <button class="btn btn-primary" 
                                @click.prevent="handleFormButtonClickPrimary(form)" 
                                :disabled="isButtonDisabled(form)"
                            >
                                {{form.label}}
                            </button>
                        </div>
                        <div class="form-element" v-if="form.type == 'button-secondary'">
                            <button class="btn btn-secondary" 
                                @click.prevent="handleFormButtonClickSecondary(form)" 
                                :disabled="isButtonDisabled(form)"
                            >
                                {{form.label}}
                            </button>
                        </div>
                        <div class="form-element short-element" v-if="form.type == 'input-text'">
                            <label for="">
                                <span class="label-title">{{form.fields.label}}</span>
                                <span v-if="form.fields.required" class="required-field">*</span>
                            </label>
                            <input-text :ref="form.fields.name" v-model="form.fields.value" :field-data="form.fields"></input-text>
                        </div>
                    </div>
                </template>
                <div v-if="autoCheckEnabled" class="auto-check-message">Checking for updates in {{autoCheckFrequency - autoCheckFequencyCount}} seconds...<br><a class="link-button" @click.prevent="checkForUpdates">Check Now</a></div>
                <div class="form-processing d-flex justify-content-center align-items-center" v-if="isProcessing"><span class="fa fa-cog fa-spin fa-3x"></span></div>
        </div>
    </div>
</template>

<script>

import { gsap } from "gsap";
import axios from 'axios';

import InputText from '../form/form-text.vue';
import StepsOverview from './stepsProgressOverview.vue';

let intervalId = 0;

export default {
    name: 'channel-connect',
    components: {
        'input-text': InputText,
        'steps-overview': StepsOverview,
    },
    created() {
        this.getChannelConnectionState();

        // a timer used to keep track of seconds between update checks.
        intervalId = window.setInterval(() => {this.autoCheckFequencyCountUpdate()}, 1000);
    },
    props: {
        channelId: Number,
    },
    data() {
        return {
            connectionTitle: '',
            stepId: "",
            steps: [],
            updatedData: {},
            isError: false,
            errorMessage: '',
            isProcessing: false,
            autoCheckFrequency: 15,
            autoCheckFequencyCount: 0,
            autoCheckEnabled: false,
        };
    },
    methods: {
        getChannelConnectionState() {

            this.isProcessing = true;

            axios.get(`/channels/${this.channelId}/connection`)
            .then((response) => {
                this.isProcessing = false;

                this.handleUpdateSteps(response.data);
            });

        },
        isAutoCheckEnabled() {
            // Check if periodic backend update calls should be triggered (autoCheckEnabled=true).
            // These should only be happening if there are no form buttons on the current
            // step and this is not the last step in the setup.

            // check for buttons on the page
            const hasButtons = (this.getCurrentStepFormArray().filter(item => {
                return (item.type == "button-primary" || item.type == "button-secondary");
            }).length > 0);

            // check to see if this is the last step in the sequence
            let isLastStep = false;
            if (this.steps.length > 0) {
                if (this.stepId == this.steps[this.steps.length-1].id) {
                    isLastStep = true;
                }
            }

            // no buttons on this page and this is NOT the last step
            if (!hasButtons && !isLastStep) {
                this.autoCheckEnabled = true;
            } else {
                this.autoCheckEnabled = false;
            }

        },
        autoCheckFequencyCountUpdate() {
            if (this.autoCheckEnabled && !this.isProcessing) {
                this.autoCheckFequencyCount++;
                if (this.autoCheckFequencyCount >= this.autoCheckFrequency) {
                    this.autoCheckFequencyCount = 0;
                    this.checkForUpdates();
                }
            } else {
                this.autoCheckFequencyCount = 0;
            }
        },
        checkForUpdates() {
            this.getChannelConnectionState();
        },
        handleFormButtonClickPrimary(form) {
            this.isError = false;

            // NOTE: currently only posting form value of type 'input-text'. If we add more
            // form input types in the future this filter will need to be updated.
            const formInputsArray = this.getCurrentStepFormArray().filter(item => {
                return item.type == "input-text"
            });

            let postObj = {};

            formInputsArray.forEach(element => {
                postObj[element.fields.name] = element.fields.value;
            });

            this.isProcessing = true;

            axios.post(`/channels/${this.channelId}/connection`, postObj)
            .then((result) => {
                this.isProcessing = false;
                if (result.data.status == 200) {
                    this.handleUpdateSteps(result.data.data);
                } else {
                    this.handleDataGetError(result.data.msg);
                }
            })
            .catch((error) => {
                this.isProcessing = false;
                this.handleDataGetError(error.message);
            });
        },
        handleUpdateSteps(data) {

            data.steps.forEach((step, index) => {
                this.$set(this.steps, index, step);
            });

            this.stepId = data.stepId;
            this.isAutoCheckEnabled();

        },
        handleDataGetError(error) {
            gsap.to(".form-step-wrapper", 0.15, {opacity: 1, stagger: 0.1} );
            this.isError = true;
            this.errorMessage = error
        },
        getCurrentStepFormArray() {

            if (this.steps.length == 0) {
                return []
            }

            let currentStep = this.steps.filter(step => {
                return (step.id == this.stepId);
            });

            if (currentStep && currentStep.length > 0 && currentStep[0].hasOwnProperty('forms')) {
                return currentStep[0].forms;
            }

            return []

        },
        isButtonDisabled(form) {
            if (form.hasOwnProperty('enabledRequirements')) {
                if (form.enabledRequirements.hasOwnProperty('textFieldHasContent')) {
                    const formName = form.enabledRequirements.textFieldHasContent;

                    if (this.$refs.hasOwnProperty(formName) && this.$refs[formName].length > 0) {
                        return this.$refs[formName][0].field.value === "";
                    } else {
                        //could not find reference to text field. Setting disabled as true.
                        return true;
                    }
                }
                //unknown type of requirement check type. Setting disable flag to false.
                return false;
            } else {
                //no requirements to enable button. Setting disable flag to false.
                return false;
            }
        },
    }
}
</script>

<style>

.link-button {
    font-weight: 600;
    text-decoration: underline;
    cursor: pointer;
}

.slide-enter-active, .slide-leave-active {
    transition: opacity .5s;
}

.slide-enter, .slide-leave-to {
  opacity: 1;
}

div.forms-wrapper .btn-primary {
    background-color: #00B0D8 !important;
    border-color: #00B0D8 !important;
    font-weight: 400;
}

div.forms-wrapper .btn-secondary {
    background-color: #576670 !important;
    border-color: #576670 !important;
    color: white;
    font-weight: 400;
}

.form-element {
    margin-bottom: 20px;
}

.short-element {
    max-width: 500px;
}

.connect-label {
    font-size: 18px;
    color: #576670;
}

.forms-wrapper {
    position: relative;
}

.channel-connect-wrapper {
    padding-bottom: 30px;
}

.form-error {
    color: #AB5755;
    font-weight: 500;
}

.form-error-msg {
    font-family: monospace;
    padding-bottom: 25px;
}

.form-processing {
    position: absolute;
    top:0;
    left:0;
    width: 100%;
    height: 100%;
    background: white;
}

.link-important {
    font-size: 1.1em;
    text-decoration: underline;
}

</style>
